import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { Loader2 } from "lucide-react";
import { isAuthenticated } from "@/lib/supabase";
import { LoginModal } from "./LoginModal";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const [, setLocation] = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [isAuth, setIsAuth] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    // Check if auth is enabled via env var (defaults to true)
    const enableAuth = import.meta.env.VITE_ENABLE_AUTH !== 'false';

    if (!enableAuth) {
      setIsAuth(true);
      setIsChecking(false);
      return;
    }

    setIsChecking(true);

    // Safety timeout to prevent infinite loading
    const safetyTimeout = setTimeout(() => {
      if (isChecking) {
        console.warn("Auth check timed out in ProtectedRoute");
        setIsChecking(false);
        setShowLoginModal(true);
      }
    }, 10000);

    try {
      const authenticated = await isAuthenticated();
      clearTimeout(safetyTimeout);
      setIsAuth(authenticated);

      if (!authenticated) {
        setShowLoginModal(true);
      }
    } catch (error) {
      clearTimeout(safetyTimeout);
      console.error("Auth check failed:", error);
      setShowLoginModal(true);
    } finally {
      setIsChecking(false);
    }
  };

  const handleLoginSuccess = () => {
    setShowLoginModal(false);
    setIsAuth(true);
  };

  const handleLoginClose = () => {
    // If user closes login modal without authenticating, redirect to landing
    if (!isAuth) {
      setLocation("/");
    }
  };

  // Show loading spinner while checking authentication
  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted/30">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Show login modal if not authenticated
  if (!isAuth) {
    return (
      <LoginModal
        open={showLoginModal}
        onClose={handleLoginClose}
        onSuccess={handleLoginSuccess}
      />
    );
  }

  // User is authenticated, render the protected content
  return <>{children}</>;
}

